/**
 * KAN-2093 — the "Pre-filled from …" note above the checkout form.
 *
 * Measured on production: the page said "Předvyplněno z vašeho profilu"
 * while the company came from the previous order. Since variant B the
 * address may come from one place (address book / last order / profile)
 * and the company (address book / company account / profile) or the
 * phone (profile) from another — the note names each source it used
 * instead of claiming one for all.
 */

import type { CheckoutPrefill } from "@/api/types";

type Part = "address" | "company" | "phone";

type Translate = (key: string, options?: Record<string, unknown>) => string;

function present(src: string | null | undefined): string | null {
  return src && src !== "none" ? src : null;
}

/** ``null`` when nothing was pre-filled (the FE shows no note). */
export function checkoutPrefillNote(
  prefill:
    | Pick<CheckoutPrefill, "source" | "company_source" | "phone_source">
    | null
    | undefined,
  t: Translate,
  lang: string,
): string | null {
  if (!prefill) return null;
  const parts: Array<[Part, string]> = [];
  const address = present(prefill.source);
  const company = present(prefill.company_source);
  const phone = present(prefill.phone_source);
  if (address) parts.push(["address", address]);
  if (company) parts.push(["company", company]);
  if (phone) parts.push(["phone", phone]);
  if (parts.length === 0) return null;

  const sources = [...new Set(parts.map(([, src]) => src))];
  if (sources.length === 1) return t(`prefilledFrom.${sources[0]}`);

  let list: (items: string[]) => string;
  try {
    const fmt = new Intl.ListFormat(lang, { type: "conjunction" });
    list = (items) => fmt.format(items);
  } catch {
    list = (items) => items.join(", ");
  }
  const groups = sources.map((src) => {
    const labels = parts
      .filter(([, s]) => s === src)
      .map(([part]) => t(`prefilledPart.${part}`));
    return `${list(labels)} ${t(`prefilledFromShort.${src}`)}`;
  });
  return t("prefilledMixed", { parts: groups.join("; ") });
}
